import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { axiosInstance } from '../../helpers'
import {
    ApplicationFeePaymentStatus,
    PaginatedApplicationFeePaymentsResponse,
} from '../../models/Outreach/types'

export interface ApplicationFeePaymentsFilters {
    page?: number
    page_size?: number
    leasing_season_id?: number
    paid_at_start?: string
    paid_at_end?: string
    status?: ApplicationFeePaymentStatus | ''
}

const DEFAULT_FILTERS: ApplicationFeePaymentsFilters = {
    page: 1,
    page_size: 25,
    status: 'succeeded',
}

export const useApplicationFeePayments = (
    initialFilters: ApplicationFeePaymentsFilters = {},
) => {
    const [filters, setFilters] = useState<ApplicationFeePaymentsFilters>({
        ...DEFAULT_FILTERS,
        ...initialFilters,
    })

    const queryParams = useMemo(() => {
        const params: Record<string, string | number> = {
            page: filters.page ?? 1,
            page_size: filters.page_size ?? 25,
        }
        if (filters.leasing_season_id != null) {
            params.leasing_season_id = filters.leasing_season_id
        }
        if (filters.paid_at_start) {
            params.paid_at_start = filters.paid_at_start
        }
        if (filters.paid_at_end) {
            params.paid_at_end = filters.paid_at_end
        }
        if (filters.status) {
            params.status = filters.status
        }
        return params
    }, [filters])

    const query = useQuery({
        queryKey: ['application-fee-payments', queryParams],
        queryFn: async () => {
            const {
                data,
            } = await axiosInstance.get<PaginatedApplicationFeePaymentsResponse>(
                '/outreach/application-fee-payments/',
                { params: queryParams },
            )
            return data
        },
        refetchOnWindowFocus: false,
    })

    const pageSize = filters.page_size ?? 25
    const currentPage = filters.page ?? 1
    const totalPages = query.data
        ? Math.max(1, Math.ceil(query.data.count / pageSize))
        : 1

    return {
        data: query.data,
        isLoading: query.isLoading,
        error: query.error,
        filters,
        setFilters,
        pagination: {
            count: query.data?.count ?? 0,
            next: query.data?.next ?? null,
            previous: query.data?.previous ?? null,
            currentPage,
            pageSize,
            totalPages,
        },
        refetch: query.refetch,
    }
}
